import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import QRCode from 'qrcode';
import Button from '../components/Button';
import { APP_NAME } from '../constants';
import logoImage from '../assets/logo.png';
import backgroundImage from '../assets/home-page-background.jpg';
import appstoreButton from '../assets/appstore-button.png';
import playstoreButton from '../assets/playstore-button.png';

// Styled components (reusing similar styles from LandingPage)
const PageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BackgroundImage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  z-index: -1;
  filter: brightness(0.7);
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
`;

const Logo = styled.img`
  height: 50px;
  cursor: pointer;
  
  @media (max-width: 480px) {
    height: 40px;
  }
`;

const ProfileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  i {
    font-size: 22px;
    color: #381f74;
  }
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    
    i {
      font-size: 20px;
    }
  }
`;

const ContentContainer = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 30px;
  border-radius: 20px;
  z-index: 1;
  margin: 80px auto 40px;
  
  @media (max-width: 768px) {
    padding: 20px;
    width: 90%;
    margin-top: 60px;
  }
`;

const MainHeading = styled.h1`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 480px) {
    font-size: 30px;
  }
`;

const SubHeading = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #ecb541;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 18px;
  margin-bottom: 30px;
  line-height: 1.6;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const CodeCard = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  border: 2px dashed white;
  border-radius: 15px;
  padding: 20px;
  margin: 30px auto;
  width: fit-content;
  max-width: 400px;
  min-width: 280px;
  
  @media (max-width: 480px) {
    padding: 15px;
    min-width: 240px;
  }
`;

const CodeText = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: #ecb541;
  letter-spacing: 2px;
  margin-bottom: 0;
  
  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const ButtonContainer = styled.div`
  width: fit-content;
  max-width: 400px;
  min-width: 280px;
  margin: 20px auto;
  
  @media (max-width: 480px) {
    min-width: 240px;
  }
`;

const QRContainer = styled.div`
  background-color: white;
  border-radius: 15px;
  padding: 10px;
  margin: 30px auto;
  display: inline-block;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const SectionHeading = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin: 30px 0 15px;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const InstructionParagraph = styled.p`
  font-size: 16px;
  margin-bottom: 20px;
  line-height: 1.6;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const AppStoreContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const AppStoreImage = styled.img`
  height: 50px;
  
  @media (max-width: 480px) {
    height: 40px;
  }
`;

const BetaMessage = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: white;
  font-size: 18px;
`;

const ErrorContainer = styled.div`
  text-align: center;
  color: white;
  padding: 40px;
  
  h2 {
    color: #ff6b6b;
    margin-bottom: 20px;
  }
`;

interface GiftCard {
  amount: number;
  businessId: string;
  businessName: string;
  code: string;
  recipientEmail: string;
  status: string;
  senderEmail: string;
}

interface Business {
  name: string;
  address: string;
  city: string;
  province: string;
}

const GiftCardPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [creditCode, setCreditCode] = useState<GiftCard | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [senderName, setSenderName] = useState<string>('');

  useEffect(() => {
    const fetchCreditCodeData = async () => {
      if (!code) {
        setError('Invalid credit code');
        setLoading(false);
        return;
      }

      try {
        // Convert code to uppercase for case-insensitive search
        const uppercaseCode = code.toUpperCase();
        
        // Query gift_cards collection for the code
        const giftCardsRef = collection(db, 'gift_cards');
        const creditCodeQuery = query(giftCardsRef, where('code', '==', uppercaseCode), limit(1));
        const creditCodeSnapshot = await getDocs(creditCodeQuery);
        
        if (creditCodeSnapshot.empty) {
          setError('Credit code not found');
          setLoading(false);
          return;
        }

        const creditCodeDoc = creditCodeSnapshot.docs[0];
        const creditCodeData = creditCodeDoc.data() as GiftCard;
        setCreditCode(creditCodeData);

        // Fetch business data
        const businessDoc = await getDoc(doc(db, 'businesses', creditCodeData.businessId));
        if (businessDoc.exists()) {
          setBusiness(businessDoc.data() as Business);
        }

        // Extract sender name from email if it's a user email
        if (creditCodeData.senderEmail && !creditCodeData.senderEmail.startsWith('business:') && !creditCodeData.senderEmail.startsWith('api_key:')) {
          // Try to get user data
          const usersRef = collection(db, 'users');
          const userQuery = query(usersRef, where('email', '==', creditCodeData.senderEmail), limit(1));
          const userSnapshot = await getDocs(userQuery);
          
          if (!userSnapshot.empty) {
            const userData = userSnapshot.docs[0].data();
            setSenderName(`${userData.firstName || ''} ${userData.lastName || ''}`.trim());
          }
        }

        // Generate QR code
        const qrDataURL = await QRCode.toDataURL(uppercaseCode, {
          width: 200,
          margin: 2,
        });
        setQrCodeDataURL(qrDataURL);

      } catch (err) {
        console.error('Error fetching credit code data:', err);
        setError('Failed to load credit code data');
      } finally {
        setLoading(false);
      }
    };

    fetchCreditCodeData();
  }, [code]);

  const handleCopyCode = async () => {
    if (code) {
      try {
        await navigator.clipboard.writeText(code);
        alert('Code copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    }
  };

  const handleShareLink = async () => {
    if (code) {
      const shareUrl = `https://storecreditapp.web.app/share/code/${code}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy share link:', err);
      }
    }
  };

  const handleProfile = () => {
    navigate('/login');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <PageContainer>
        <BackgroundImage />
        <Header>
          <Logo src={logoImage} alt="Logo" onClick={handleLogoClick} />
          <ProfileIcon onClick={handleProfile}>
            <i className='bx bx-user'></i>
          </ProfileIcon>
        </Header>
        <LoadingContainer>
          Loading credit code...
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !creditCode) {
    return (
      <PageContainer>
        <BackgroundImage />
        <Header>
          <Logo src={logoImage} alt="Logo" />
          <ProfileIcon onClick={handleProfile}>
            <i className='bx bx-user'></i>
          </ProfileIcon>
        </Header>
        <ContentContainer>
          <ErrorContainer>
            <h2>Credit Code Not Found</h2>
            <p>{error || 'The credit code you entered is invalid or has expired.'}</p>
          </ErrorContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`$${creditCode.amount.toFixed(2)} Store Credit at ${business?.name || creditCode.businessName} - ${APP_NAME}`}</title>
        <meta name="description" content={`Use the ${APP_NAME} to claim $${creditCode.amount.toFixed(2)} in store credit at ${business?.name || creditCode.businessName}${business?.address ? ` located at ${business.address}, ${business.city}, ${business.province}` : ''}`} />
        <meta property="og:title" content={`$${creditCode.amount.toFixed(2)} Store Credit at ${business?.name || creditCode.businessName}`} />
        <meta property="og:description" content={`Use the ${APP_NAME} to claim $${creditCode.amount.toFixed(2)} in store credit at ${business?.name || creditCode.businessName}${business?.address ? ` located at ${business.address}, ${business.city}, ${business.province}` : ''}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://storecreditapp.web.app/code/${code}`} />
        <meta property="og:site_name" content={APP_NAME} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={`$${creditCode.amount.toFixed(2)} Store Credit at ${business?.name || creditCode.businessName}`} />
        <meta property="twitter:description" content={`Use the ${APP_NAME} to claim $${creditCode.amount.toFixed(2)} in store credit at ${business?.name || creditCode.businessName}${business?.address ? ` located at ${business.address}, ${business.city}, ${business.province}` : ''}`} />
      </Helmet>
      
      <PageContainer>
        <BackgroundImage />
        
        <Header>
          <Logo src={logoImage} alt="Logo" />
          <ProfileIcon onClick={handleProfile}>
            <i className='bx bx-user'></i>
          </ProfileIcon>
        </Header>
        
        <ContentContainer>
          <MainHeading>
            ${creditCode.amount.toFixed(2)} Store Credit
          </MainHeading>
          
          <SubHeading>at {business?.name || creditCode.businessName}</SubHeading>
          
          {creditCode.status !== 'active' && (
            <SubHeading style={{ color: '#ff6b6b', marginBottom: '10px' }}>
              REDEEMED
            </SubHeading>
          )}
          
          <Paragraph>
            Use the {APP_NAME} to claim <strong>${creditCode.amount.toFixed(2)}</strong> in store credit at <strong>{business?.name || creditCode.businessName}</strong> located at             {[business?.address, business?.city, business?.province].filter(Boolean).map((segment, index, arr) => (
              <span key={index}>
                <strong>{segment}</strong>
                {index < arr.length - 1 && ', '}
              </span>
            ))}{senderName && (
              <> courtesy of <strong>{senderName}</strong></>
            )}.
          </Paragraph>

            <CodeCard>
            <CodeText>
              {creditCode.code}
              <i 
              className='bx bx-copy' 
              onClick={handleCopyCode}
              style={{
                marginLeft: '10px',
                cursor: 'pointer',
                fontSize: '24px',
                color: 'white',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ecb541'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'white'}
              />
            </CodeText>
            </CodeCard>

          {/* <ButtonContainer>
            <Button
              text="Copy Code"
              onClick={handleCopyCode}
              bgColor="#ecb541"
              textColor="#381f74"
            />
          </ButtonContainer> */}

          {/* <ButtonContainer>
            <Button
              text="Share Link"
              onClick={handleShareLink}
              bgColor="#381f74"
              textColor="white"
            />
          </ButtonContainer> */}

          {qrCodeDataURL && (
            <QRContainer style={{ width: 'fit-content', maxWidth: '400px', minWidth: '280px', aspectRatio: '1/1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={qrCodeDataURL} alt="QR Code" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </QRContainer>
          )}

          <SectionHeading>How To Claim Credit</SectionHeading>
          <InstructionParagraph>
            Open {APP_NAME} and tap on the "Claim Credit" button. Next choose to enter the code or scan the QR code above.
          </InstructionParagraph>

          <SectionHeading>Get The App</SectionHeading>
          
          <AppStoreContainer>
            <a href="https://play.google.com/store/apps/details?id=com.brownvincicode24.storecreditappc" target="_blank" rel="noopener noreferrer">
              <AppStoreImage 
                src={playstoreButton}
                alt="Get it on Google Play" 
              />
            </a>
            
            <a href="https://apps.apple.com/us/app/store-credit/id6746509517" target="_blank" rel="noopener noreferrer">
              <AppStoreImage 
                src={appstoreButton}
                alt="Download on the App Store" 
              />
            </a>
          </AppStoreContainer>
          
          <BetaMessage>
            {APP_NAME} is currently in BETA. If you run into any problems or have any
            questions or concerns, don't hesitate to <a href="/contact" style={{ color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}>contact us</a>.
          </BetaMessage>
        </ContentContainer>
      </PageContainer>
    </>
  );
};

export default GiftCardPage;
